import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle,
  X,
  Loader2,
  UserX,
  ChevronLeft,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../../../lib/supabase';
import { logEvent } from '../../../../services/loggingService';
import { clearUserLocalData } from '../../../../utils/clearUserData';

interface AccountDeletionFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

type DeletionStep =
  | 'confirmation'
  | 'processing'
  | 'success';

interface DeletionData {
  reasons: string[];
  feedback: string;
  rating: number | null;
  ratingComment: string;
  wantsReconnect: boolean;
  email: string;
  alternativeConsidered: boolean;
}

const AccountDeletionFlow: React.FC<AccountDeletionFlowProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<DeletionStep>('confirmation');
  const [deletionData, setDeletionData] = useState<DeletionData>({
    reasons: [],
    feedback: '',
    rating: null,
    ratingComment: '',
    wantsReconnect: false,
    email: '',
    alternativeConsidered: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user email
  useEffect(() => {
    const loadUserEmail = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          setDeletionData(prev => ({ ...prev, email: user.email || '' }));
        }
      } catch (error) {
        console.error('Error loading user email:', error);
      }
    };
    
    if (isOpen) {
      loadUserEmail();
    }
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('confirmation');
      setDeletionData({
        reasons: [],
        feedback: '',
        rating: null,
        ratingComment: '',
        wantsReconnect: false,
        email: '',
        alternativeConsidered: false
      });
      setError(null);
    }
  }, [isOpen]);

  // Handle final deletion
  const handleFinalDeletion = async () => {
    setLoading(true);
    setError(null);

    try {
      // Log deletion attempt with all collected data
      await logEvent({
        level: 'info',
        action: 'account_deletion_initiated',
        details: {
          reasons: deletionData.reasons,
          hasFeedback: !!deletionData.feedback,
          rating: deletionData.rating,
          wantsReconnect: deletionData.wantsReconnect,
          alternativeConsidered: deletionData.alternativeConsidered
        }
      });

      // Store reconnect preference if user wants it
      if (deletionData.wantsReconnect && deletionData.email) {
        try {
          // Store in a simple table for future reconnect campaigns
          await supabase
            .from('user_reconnect_preferences')
            .insert({
              email: deletionData.email,
              reasons: deletionData.reasons,
              feedback: deletionData.feedback,
              wants_reconnect: true,
              deleted_at: new Date().toISOString()
            });
        } catch (reconnectError) {
          console.warn('Failed to store reconnect preference:', reconnectError);
          // Don't fail deletion for this
        }
      }

      // Call the deletion API
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Authentication required');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-data`;
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deletionData: deletionData,
          confirmDeletion: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('dashboard.userProfile.accountDeletion.failedToDeleteAccount'));
      }

      // 📧 Send account deletion farewell email (fire-and-forget before session is cleared)
      const userEmail = deletionData.email;
      if (userEmail) {
        supabase.functions.invoke('send-funnel-email', {
          body: { type: 'account_deleted', email: userEmail },
        }).catch(() => {});
      }

      // Clear all user-specific local/session storage so the next user
      // on this device doesn't see stale calendar entries, notes, etc.
      clearUserLocalData();

      setCurrentStep('success');

      // Auto-redirect after success
      setTimeout(() => {
        onComplete();
      }, 5000);

    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err instanceof Error ? err.message : t('dashboard.userProfile.accountDeletion.failedToDeleteAccount'));
    } finally {
      setLoading(false);
    }
  };

  const isLocked = currentStep === 'processing' || currentStep === 'success';
  const stepIndex = ['confirmation', 'processing', 'success'].indexOf(currentStep) + 1;

  const renderStep = () => {
    switch (currentStep) {
      case 'confirmation':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="vscode-text-secondary text-sm">{t('dashboard.userProfile.accountDeletion.confirmWillRemove')}</p>
              <ul className="space-y-1.5 pl-1">
                {([
                  t('dashboard.userProfile.accountDeletion.itemProfile'),
                  t('dashboard.userProfile.accountDeletion.itemResumes'),
                  t('dashboard.userProfile.accountDeletion.itemApplications'),
                  t('dashboard.userProfile.accountDeletion.itemObjectives'),
                ] as string[]).map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm vscode-text-secondary">
                    <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: 'var(--vscode-text-secondary, #9ca3af)' }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs vscode-text-tertiary">{t('dashboard.userProfile.accountDeletion.permanentWarning')}</p>
            <p className="vscode-text-primary text-sm font-medium">{t('dashboard.userProfile.accountDeletion.areYouSure')}</p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setCurrentStep('processing')}
                className="flex-1 min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation border"
                style={{ borderColor: 'var(--vscode-error, #ef4444)', color: 'var(--vscode-error, #ef4444)' }}
              >
                {t('dashboard.userProfile.accountDeletion.delete')}
              </button>
              <button
                onClick={onClose}
                className="flex-1 min-h-[38px] px-3 py-2 vscode-bg-tertiary hover:vscode-bg-hover border vscode-border rounded-lg vscode-text-primary text-sm font-medium transition-colors touch-manipulation"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin shrink-0" style={{ color: 'var(--vscode-primary)' }} />
              <div>
                <p className="vscode-text-primary text-sm font-medium">{t('dashboard.userProfile.accountDeletion.deletingYourAccount')}</p>
                <p className="vscode-text-tertiary text-xs mt-0.5">{t('dashboard.userProfile.accountDeletion.pleaseWait')}</p>
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 vscode-bg-tertiary border vscode-border rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--vscode-error, #ef4444)' }} />
                <div>
                  <p className="vscode-text-primary text-sm font-medium">{t('dashboard.userProfile.accountDeletion.deletionFailed')}</p>
                  <p className="vscode-text-tertiary text-xs mt-0.5">{error}</p>
                  <button onClick={() => setCurrentStep('confirmation')} className="underline vscode-text-secondary text-xs mt-1">
                    {t('dashboard.userProfile.accountDeletion.tryAgain')}
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'success':
        return (
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'var(--vscode-success, #22c55e)' }} />
            <div>
              <p className="vscode-text-primary text-sm font-medium">{t('dashboard.userProfile.accountDeletion.accountDeleted')}</p>
              <p className="vscode-text-tertiary text-xs mt-1 leading-relaxed">{t('dashboard.userProfile.accountDeletion.accountDeletedDesc')}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Auto-start deletion process when step changes to processing
  useEffect(() => {
    if (currentStep === 'processing' && !loading) {
      handleFinalDeletion();
    }
  }, [currentStep]);

  const isNative = Capacitor.isNativePlatform();

  const portalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: isNative ? 40 : 0 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: isNative ? 40 : 0 }}
          transition={{ duration: 0.2 }}
          style={isNative
            ? { pointerEvents: 'all', position: 'fixed', inset: 0, zIndex: 9999, background: 'var(--vscode-bg-primary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
            : { pointerEvents: 'all' }
          }
          className={isNative ? undefined : "fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"}
          onClick={isNative ? undefined : (isLocked ? undefined : onClose)}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ scale: isNative ? 1 : 0.96, opacity: 0, y: isNative ? 0 : 6 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: isNative ? 1 : 0.96, opacity: 0, y: isNative ? 0 : 6 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className={isNative
              ? "flex flex-col flex-1 min-h-0 w-full overflow-hidden"
              : "vscode-bg-secondary rounded-xl border vscode-border w-full max-w-sm mx-4 overflow-hidden shadow-xl"
            }
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title bar — native header or web compact bar */}
            {isNative ? (
              <div
                style={{
                  background: 'var(--brand-popup-header)',
                  paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
                  paddingBottom: 22, paddingLeft: 20, paddingRight: 20,
                  display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
                }}
              >
                {!isLocked && (
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      background: 'rgba(255,255,255,0.18)', border: 'none',
                      borderRadius: 10, width: 36, height: 36,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent',
                    }}
                    aria-label="Go back"
                  >
                    <ChevronLeft size={20} color="white" />
                  </button>
                )}
                <div style={{ flex: 1 }}>
                  <h1 style={{ fontSize: 20, fontWeight: 800, color: 'white', margin: 0, letterSpacing: '-0.02em' }}>
                    Delete Account
                  </h1>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.70)', margin: '3px 0 0' }}>
                    This action is permanent and cannot be undone
                  </p>
                </div>
              </div>
            ) : (
              <div
                className="flex items-center justify-between px-3 py-2 shrink-0 select-none"
                style={{ background: 'var(--brand-popup-header)', borderBottom: '1px solid var(--brand-popup-header-border)' }}
              >
                <div className="flex items-center gap-2">
                  <UserX className="w-4 h-4" style={{ color: 'var(--brand-cta-text)', opacity: 0.8 }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--brand-cta-text)' }}>{t('dashboard.userProfile.accountDeletion.title')}</span>
                </div>
                {!isLocked && (
                  <button
                    onClick={onClose}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/20 transition-colors touch-manipulation"
                    aria-label="Close"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* Progress bar */}
            <div className="h-0.5 vscode-bg-tertiary" style={{ flexShrink: 0 }}>
              <motion.div
                className="h-full"
                style={{ background: 'var(--brand-cta-bg)' }}
                animate={{ width: `${(stepIndex / 3) * 100}%` }}
                transition={{ duration: 0.35 }}
              />
            </div>

            {/* Content */}
            <div
              className={isNative ? "flex-1 overflow-y-auto" : ""}
              style={isNative ? {
                padding: 20,
                paddingBottom: 'max(env(safe-area-inset-bottom), 20px)',
              } : undefined}
            >
              <div className={isNative ? "" : "p-4"}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    {renderStep()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(portalContent, document.body);
};

export default React.memo(AccountDeletionFlow);
